import { z } from 'zod';

const isoDateTime = () => z.string().datetime({ offset: true });

export const clientSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).optional().nullable(),
  firstName: z.string().trim().min(1).optional().nullable(),
  lastName: z.string().trim().min(1).optional().nullable(),
  managerName: z.string().trim().min(1).optional().nullable(),
  contactInfo: z.string().trim().min(1).optional().nullable(),
  overdueAfterDays: z.number().int().min(1).max(365).optional().nullable(),
  apEmails: z.array(z.string().trim().email()).optional(),
  createdAt: isoDateTime(),
  updatedAt: isoDateTime(),
});

/**
 * POST /clients/:id/ap-emails — add ONE address to the client's AP list.
 *
 * Separate from `clientUpdateSchema.apEmails`, which REPLACES the whole list:
 * the send dialog knows the one address it wants added and not what else is on
 * the list, and sending a list it assembled from a cached read is how one tab
 * silently drops an address another tab just added.
 */
export const addApEmailSchema = z.object({
  email: z.string().trim().email(),
});

export type AddApEmailInput = z.infer<typeof addApEmailSchema>;

const clientInputBaseSchema = z.object({
  name: z.string().trim().min(1).optional().or(z.literal('').transform(() => undefined)),
  firstName: z.string().trim().min(1).optional().or(z.literal('').transform(() => undefined)),
  lastName: z.string().trim().min(1).optional().or(z.literal('').transform(() => undefined)),
  managerName: z.string().trim().min(1).optional(),
  contactInfo: z.string().trim().min(1).optional(),
  // FR-1.4 per-client override. Null/absent = inherit the company default.
  overdueAfterDays: z.number().int().min(1).max(365).optional().nullable(),
  // FR-4.2 client-level AP addresses (spec §3, D3). On update: absent = unchanged, [] = cleared.
  apEmails: z.array(z.string().trim().email()).optional(),
});

export const clientInputSchema = clientInputBaseSchema.refine(
  (data) => data.name || data.firstName || data.lastName,
  { message: 'Either company name or individual name (first/last) is required' }
);

// For updates, empty strings should become null to clear fields
const clientUpdateBaseSchema = z.object({
  name: z.string().trim().transform(v => v || null).optional().nullable(),
  firstName: z.string().trim().transform(v => v || null).optional().nullable(),
  lastName: z.string().trim().transform(v => v || null).optional().nullable(),
  managerName: z.string().trim().transform(v => v || null).optional().nullable(),
  contactInfo: z.string().trim().transform(v => v || null).optional().nullable(),
  overdueAfterDays: z.number().int().min(1).max(365).nullable().optional(),
  apEmails: z.array(z.string().trim().email()).optional(),
});

export const clientUpdateSchema = clientUpdateBaseSchema.partial();
